import { FaPlus } from 'react-icons/fa'
import React, { useState } from 'react'
import { NotesTable } from 'components/Tables/NotesTable/NotesTable'
import NotesModal from '../../Modals/NotesModal'
import { CollapsibleSection } from '../../UI/CollapsibleSection/CollapsibleSection'
import FbButton from '../../FbUI/FbButton'
import { formatInteger } from '../../../utils/formatting'
import { queryClient } from '../../../services/queryClient'
import { FilterIdentifier } from '../../../models/saved_view'
import NotesFilterset from 'components/Tables/NotesTable/NotesFilterset'
import { Tabs } from '../../FbUI/Tabs'
import apiService from 'services/api'

interface NotesTableWrapperProps {
  tableKey: string
  filterIdentifier: FilterIdentifier
  showDoorColumns: boolean
  setCount: (count: number) => void
  showDoorFilters?: boolean
  noteType?: 'company' | 'door'
  contactCompanyId?: number
  chainId?: number
  doorProxyId?: number
  enablePagination?: boolean
  effectiveContactCompanyId?: number
}

function NotesTableWrapper({
  tableKey,
  filterIdentifier,
  showDoorColumns,
  setCount,
  showDoorFilters,
  noteType,
  contactCompanyId,
  chainId,
  doorProxyId,
  enablePagination,
  effectiveContactCompanyId,
}: NotesTableWrapperProps) {
  // Hide country, state, and city columns by default for door notes
  const defaultColumnVisibility = showDoorColumns
    ? {
        door_country: false, // This will hide the country column
        door_city: false, // This will hide the city column
        door_state: false, // This will hide the state column
      }
    : undefined

  const savedPresetsIdentifier = showDoorColumns
    ? 'DOOR_NOTES_TABLE'
    : 'NOTES_TABLE'

  return (
    <NotesTable
      tableKey={tableKey}
      filterIdentifier={filterIdentifier}
      defaultColumnVisibility={defaultColumnVisibility}
      savedPresetsIdentifier={savedPresetsIdentifier}
      notesFilterset={
        <NotesFilterset
          filterIdentifier={filterIdentifier}
          showDoorFilters={showDoorFilters}
          contactCompanyId={effectiveContactCompanyId}
        />
      }
      contactCompanyId={contactCompanyId}
      chainId={chainId}
      doorProxyId={doorProxyId}
      setTotalRowsCount={setCount}
      enablePagination={enablePagination}
      showDoorColumns={showDoorColumns}
      noteType={noteType}
    />
  )
}

export default function NotesSection({
  pagination,
  chainId,
  contactCompanyId,
  doorProxyId,
}: {
  chainId?: number | undefined
  contactCompanyId?: number | undefined
  doorProxyId?: number | undefined
  pagination?: boolean
}) {
  const [showModal, setShowModal] = useState(false)
  const [companyNotesCount, setCompanyNotesCount] = useState(0)
  const [doorNotesCount, setDoorNotesCount] = useState(0)
  const [selectedTab, setSelectedTab] = useState<'company' | 'door'>('company')

  const api = apiService()

  const { data: chainData } = chainId
    ? api.useGetChain(chainId)
    : { data: undefined }

  // Use contactCompanyId directly, or get it from chain data
  const effectiveContactCompanyId =
    contactCompanyId || chainData?.contact_company_id

  const isCompanyPage = !doorProxyId && (chainId || contactCompanyId)

  const COMPANY_NOTES_FILTER_IDENTIFIER =
    'COMPANY_NOTES_TABLE' as FilterIdentifier
  const DOOR_NOTES_FILTER_IDENTIFIER = 'DOOR_NOTES_TABLE' as FilterIdentifier
  const NOTES_TABLE_FILTER_IDENTIFIER = 'NOTES_TABLE' as FilterIdentifier

  const COMPANY_TABLE_KEY = `company-notes-section-${contactCompanyId || chainId}-${COMPANY_NOTES_FILTER_IDENTIFIER}`
  const DOOR_TABLE_KEY = `door-notes-section-${contactCompanyId || chainId}-${DOOR_NOTES_FILTER_IDENTIFIER}`
  const TABLE_KEY = `notes-section-${contactCompanyId || chainId || doorProxyId}-${NOTES_TABLE_FILTER_IDENTIFIER}`

  const totalCount = isCompanyPage
    ? companyNotesCount + doorNotesCount
    : companyNotesCount

  const handleModalClose = () => {
    setShowModal(false)
    if (isCompanyPage) {
      queryClient.invalidateQueries({
        queryKey: ['notes-table', COMPANY_TABLE_KEY],
      })
      queryClient.invalidateQueries({
        queryKey: ['notes-table', DOOR_TABLE_KEY],
      })
    } else {
      queryClient.invalidateQueries({ queryKey: ['notes-table', TABLE_KEY] })
    }
  }

  if (isCompanyPage) {
    return (
      <>
        <CollapsibleSection
          summary={
            <div className={'flex justify-between items-center w-full'}>
              <div>
                Notes{' '}
                <span className={'font-thin'}>
                  ({formatInteger(totalCount)})
                </span>
              </div>
              <FbButton onClick={() => setShowModal(true)}>
                <FaPlus size={14} /> Add Note
              </FbButton>
            </div>
          }
        >
          <NotesModal
            chainId={chainId}
            contactCompanyId={contactCompanyId}
            doorProxyId={doorProxyId}
            show={showModal}
            handleClose={handleModalClose}
          />

          <div className="w-full">
            <Tabs
              tabs={[
                {
                  key: 'company',
                  title: `Company Notes (${formatInteger(companyNotesCount)})`,
                },
                {
                  key: 'door',
                  title: `Door Notes (${formatInteger(doorNotesCount)})`,
                },
              ]}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />

            <div>
              <div
                className={`max-h-[580px] flex pb-1 pt-4 ${selectedTab === 'company' ? '' : 'hidden'}`}
              >
                <NotesTableWrapper
                  tableKey={COMPANY_TABLE_KEY}
                  filterIdentifier={COMPANY_NOTES_FILTER_IDENTIFIER}
                  showDoorColumns={false}
                  setCount={setCompanyNotesCount}
                  showDoorFilters={false}
                  noteType="company"
                  contactCompanyId={contactCompanyId}
                  chainId={chainId}
                  doorProxyId={doorProxyId}
                  enablePagination={
                    pagination === undefined ? true : pagination
                  }
                  effectiveContactCompanyId={effectiveContactCompanyId}
                />
              </div>

              <div
                className={`max-h-[580px] flex pb-1 pt-4 ${selectedTab === 'door' ? '' : 'hidden'}`}
              >
                <NotesTableWrapper
                  tableKey={DOOR_TABLE_KEY}
                  filterIdentifier={DOOR_NOTES_FILTER_IDENTIFIER}
                  showDoorColumns={true}
                  setCount={setDoorNotesCount}
                  showDoorFilters={true}
                  noteType="door"
                  contactCompanyId={contactCompanyId}
                  chainId={chainId}
                  doorProxyId={doorProxyId}
                  enablePagination={
                    pagination === undefined ? true : pagination
                  }
                  effectiveContactCompanyId={effectiveContactCompanyId}
                />
              </div>
            </div>
          </div>
        </CollapsibleSection>
      </>
    )
  }

  // For door pages or other cases, show the original single table
  return (
    <>
      <CollapsibleSection
        summary={
          <div className={'flex justify-between items-center w-full'}>
            <div>
              Notes{' '}
              <span className={'font-thin'}>
                ({formatInteger(companyNotesCount)})
              </span>
            </div>
            <FbButton onClick={() => setShowModal(true)}>
              <FaPlus size={14} /> Add Note
            </FbButton>
          </div>
        }
      >
        <NotesModal
          chainId={chainId}
          contactCompanyId={contactCompanyId}
          doorProxyId={doorProxyId}
          show={showModal}
          handleClose={handleModalClose}
        />

        <NotesTableWrapper
          tableKey={TABLE_KEY}
          filterIdentifier={NOTES_TABLE_FILTER_IDENTIFIER}
          showDoorColumns={false}
          setCount={setCompanyNotesCount}
          showDoorFilters={false}
          noteType={undefined}
          contactCompanyId={contactCompanyId}
          chainId={chainId}
          doorProxyId={doorProxyId}
          enablePagination={pagination === undefined ? true : pagination}
          effectiveContactCompanyId={effectiveContactCompanyId}
        />
      </CollapsibleSection>
    </>
  )
}
